import type { ClaimTask, TaskPriority } from '@/api/generated/client';

/** Filter for a task's due-date status. */
export type TaskDueFilter = 'all' | 'overdue' | 'upcoming' | 'noDueDate' | 'completed';

/** Filter for a task's priority. 'all' disables priority filtering. */
export type TaskPriorityFilter = 'all' | TaskPriority;

/**
 * Filter for a task's assignee. 'all' disables assignee filtering, 'unassigned'
 * matches tasks with no assignee, and a number matches a specific assignee's user id.
 */
export type TaskAssigneeFilter = 'all' | 'unassigned' | number;

/** A distinct assignee available to filter by, derived from a set of tasks. */
export interface TaskAssigneeOption {
  id: number;
  name: string;
}

/**
 * The subset of claim data needed to render a task's claim reference and gate
 * write actions, derived from claimNumber/insuredName/canWrite embedded on each
 * task by the backend. Deliberately narrower than the full `Claim` model (which
 * has many required fields, e.g. companyId, isArchived, that aren't available
 * here) so this type accurately reflects what's actually on hand instead of
 * misrepresenting a partial object as a complete `Claim`. claimNumber/insuredName
 * are nullable to match the task schema they're sourced from.
 */
export interface TaskClaimSummary {
  id: number;
  claimNumber: string | null;
  insuredName: string | null;
  canWrite: boolean;
}

/**
 * Formats a claim's number and insured name for display in a task's claim
 * reference (e.g. "#CLM-001 - John Doe"). Falls back to "Unknown" for either
 * field when null so the UI never renders the literal strings "null"/"undefined"
 * (claimNumber/insuredName are nullable on tasks per the API schema).
 */
export function formatClaimReference(claim: TaskClaimSummary): string {
  const number = claim.claimNumber ?? 'Unknown';
  const name = claim.insuredName ?? 'Unknown';
  return `#${number} - ${name}`;
}

const PRIORITY_ORDER: Record<TaskPriority, number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
};

/**
 * A task is "closed" (done, one way or another) once it's completed or
 * cancelled — mirrors the backend repository, which excludes both statuses
 * together (`notInArray(status, [COMPLETED, CANCELLED])`) when querying open
 * tasks. There's no dedicated "Cancelled" bucket in the UI, so cancelled tasks
 * are grouped with completed ones rather than leaking into Overdue/Upcoming.
 */
export function isTaskClosed(task: ClaimTask): boolean {
  return task.status === 'completed' || task.status === 'cancelled';
}

/**
 * A task is overdue when it has a due date strictly before today (local time)
 * and has not been completed or cancelled.
 */
export function isTaskOverdue(task: ClaimTask): boolean {
  if (!task.dueDate) return false;
  if (isTaskClosed(task)) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const dueDate = new Date(task.dueDate);
  dueDate.setHours(0, 0, 0, 0);

  return dueDate.getTime() < today.getTime();
}

/**
 * Filters tasks by due-date status:
 * - 'overdue': tasks that are past due and not completed/cancelled
 * - 'upcoming': tasks with a due date (today or later) that are not overdue and not completed/cancelled
 * - 'noDueDate': active (not completed/cancelled) tasks that have no due date
 * - 'completed': tasks that have been completed or cancelled, regardless of due date
 * - 'all': no filtering
 */
export function filterTasksByDueStatus(tasks: ClaimTask[], filter: TaskDueFilter): ClaimTask[] {
  if (filter === 'all') return tasks;
  if (filter === 'overdue') return tasks.filter(isTaskOverdue);
  if (filter === 'completed') return tasks.filter(isTaskClosed);
  if (filter === 'noDueDate') return tasks.filter((task) => !isTaskClosed(task) && !task.dueDate);
  return tasks.filter((task) => !isTaskClosed(task) && !!task.dueDate && !isTaskOverdue(task));
}

/** Filters tasks down to a single priority level, or returns all tasks when filter is 'all'. */
export function filterTasksByPriority(tasks: ClaimTask[], filter: TaskPriorityFilter): ClaimTask[] {
  if (filter === 'all') return tasks;
  return tasks.filter((task) => task.priority === filter);
}

/**
 * Filters tasks by assignee: 'all' disables filtering, 'unassigned' returns tasks
 * with no assignedTo, and a number returns tasks assigned to that user id.
 */
export function filterTasksByAssignee(tasks: ClaimTask[], filter: TaskAssigneeFilter): ClaimTask[] {
  if (filter === 'all') return tasks;
  if (filter === 'unassigned') return tasks.filter((task) => task.assignedTo == null);
  return tasks.filter((task) => task.assignedTo === filter);
}

/**
 * Returns the distinct users actually assigned to at least one of the given tasks,
 * deduped by user id and sorted alphabetically by name. Tasks with no assignedTo
 * are excluded (use the 'unassigned' TaskAssigneeFilter value to filter for those).
 * This intentionally only surfaces users who will actually have tasks appear when
 * selected — not every claim collaborator, since most of those wouldn't match any
 * tasks and would just filter the list down to nothing.
 */
export function getUniqueTaskAssignees(tasks: ClaimTask[]): TaskAssigneeOption[] {
  const byId = new Map<number, string>();
  for (const task of tasks) {
    if (task.assignedTo != null && task.assignedToName) {
      byId.set(task.assignedTo, task.assignedToName);
    }
  }
  return Array.from(byId.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Returns the given date's timestamp truncated to the start of its local day. */
function startOfDayTime(date: Date): number {
  const truncated = new Date(date);
  truncated.setHours(0, 0, 0, 0);
  return truncated.getTime();
}

/**
 * Orders tasks by due date ascending (oldest due date first). Tasks without a due
 * date always sort last. Ties — tasks due on the same calendar day (regardless of
 * time-of-day), or both missing a due date — fall back to priority order, urgent
 * first through low last.
 */
export function compareTasksByDueDate(a: ClaimTask, b: ClaimTask): number {
  const aTime = a.dueDate ? startOfDayTime(new Date(a.dueDate)) : null;
  const bTime = b.dueDate ? startOfDayTime(new Date(b.dueDate)) : null;

  if (aTime === null && bTime === null) {
    return PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
  }
  if (aTime === null) return 1;
  if (bTime === null) return -1;
  if (aTime !== bTime) return aTime - bTime;

  return PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
}

/** Returns a new array of tasks sorted by due date ascending (see compareTasksByDueDate). */
export function sortTasksByDueDate(tasks: ClaimTask[]): ClaimTask[] {
  return [...tasks].sort(compareTasksByDueDate);
}

/**
 * Orders active tasks before closed ones (completed or cancelled — see
 * isTaskClosed), then by priority (urgent first) within each group. Intended for
 * a list where every task already shares the same due date (or lack thereof) so
 * due-date ordering isn't useful, e.g. a single day's task list or the no-due-date
 * card.
 */
export function compareTasksByCompletionThenPriority(a: ClaimTask, b: ClaimTask): number {
  const aClosed = isTaskClosed(a);
  const bClosed = isTaskClosed(b);
  if (aClosed !== bClosed) return aClosed ? 1 : -1;

  return PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
}

export interface TaskFilterOptions {
  /** Defaults to 'all' (no due-date filtering) when omitted. */
  dueFilter?: TaskDueFilter;
  priorityFilter: TaskPriorityFilter;
  /** Defaults to 'all' (no assignee filtering) when omitted. */
  assigneeFilter?: TaskAssigneeFilter;
}

/** Applies due-date, priority, and assignee filters, then sorts the result by due date ascending. */
export function applyTaskFilters(
  tasks: ClaimTask[],
  { dueFilter = 'all', priorityFilter, assigneeFilter = 'all' }: TaskFilterOptions,
): ClaimTask[] {
  const byDueStatus = filterTasksByDueStatus(tasks, dueFilter);
  const byPriority = filterTasksByPriority(byDueStatus, priorityFilter);
  const byAssignee = filterTasksByAssignee(byPriority, assigneeFilter);
  return sortTasksByDueDate(byAssignee);
}
